/**
 * Client-side record of the Trello workspaces a user has previously consented
 * with, used to populate the "Recently used" group in the workspace selector.
 *
 * There is no backend source for this: the consent screen is served from a
 * stable first-party origin, so localStorage persists across consent visits for
 * every client app. Two constraints follow from how the screen works:
 *
 * - Entries are keyed by account id. The origin is shared by every Atlassian
 *   account used in the browser, so an unkeyed record would leak one user's
 *   workspaces to another.
 * - Only workspace ids are stored, never display names, so nothing
 *   human-readable is written to disk. Ids that no longer resolve against the
 *   available workspaces are dropped at render time by the selector.
 *
 * Every entry point is total: storage being unavailable (private browsing,
 * quota exhaustion, blocked cookies) degrades to "no recents" and must never
 * interrupt consent.
 */

const STORAGE_KEY_PREFIX = 'oauth-consent.recent-trello-workspaces';

export const MAX_RECENT_WORKSPACE_IDS = 5;

const storageKeyFor = (accountId: string): string =>
  `${STORAGE_KEY_PREFIX}.${accountId}`;

const getLocalStorage = (): Storage | null => {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
};

const isNonEmptyString = (value: unknown): value is string =>
  'string' === typeof value && value.length > 0;

const dedupe = (ids: readonly string[]): string[] => [...new Set(ids)];

export const readRecentWorkspaceIds = (
  accountId: string | null | undefined,
): string[] => {
  if (!isNonEmptyString(accountId)) return [];

  const storage = getLocalStorage();
  if (!storage) return [];

  try {
    const raw = storage.getItem(storageKeyFor(accountId));
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return dedupe(parsed.filter(isNonEmptyString)).slice(
      0,
      MAX_RECENT_WORKSPACE_IDS,
    );
  } catch {
    return [];
  }
};

export const clearRecentWorkspaceIds = (
  accountId: string | null | undefined,
): void => {
  if (!isNonEmptyString(accountId)) return;

  try {
    getLocalStorage()?.removeItem(storageKeyFor(accountId));
  } catch {
    // Nothing to do — the record is best-effort in both directions.
  }
};

/**
 * Records the workspaces consented to in this session, most recent first.
 *
 * Called synchronously from the Accept handler: the consent form is a real
 * document POST, so the page unloads immediately and any deferred write would
 * be lost.
 */
export const recordConsentedWorkspaceIds = (
  accountId: string | null | undefined,
  workspaceIds: readonly string[],
): void => {
  if (!isNonEmptyString(accountId)) return;

  const consented = workspaceIds.filter(isNonEmptyString);
  if (0 === consented.length) return;

  const storage = getLocalStorage();
  if (!storage) return;

  const merged = dedupe([
    ...consented,
    ...readRecentWorkspaceIds(accountId),
  ]).slice(0, MAX_RECENT_WORKSPACE_IDS);

  try {
    storage.setItem(storageKeyFor(accountId), JSON.stringify(merged));
  } catch {
    // Storage unavailable or full — recents are best-effort, never block consent.
  }
};
